import { Form, useActionData, useSubmit } from '@remix-run/react'
import React from 'react'
import {
	type Form as FormType,
	type RowField,
} from '../../../types/graphql.form.ts'
import ArrowButton from '../arrowButton.tsx'
import { Input } from '../ui/input.tsx'
import { getReCaptchaSiteKey, reCaptcha } from './captcha.tsx'
import FormField from './formField.tsx'
import Heading from './heading.tsx'
import InputEmail from './inputEmail.tsx'
import InputName from './inputName.tsx'
import InputPhone from './inputPhone.tsx'
import InputSingleLineText from './inputSingleLineText.tsx'
import TextareaMultiLineText from './textareaMultiLineText.tsx'
export { validateForm } from './validation.tsx'

const GreCAPTCHA_FORM_NAME = 'grecaptcha'

export type Error = { [key: string]: string | null } | null

export type Errors = {
	[key: string]: string | Error
}
export type FormActionResult =
	| { readonly ok: false; readonly errors: Errors }
	| { readonly ok: true; readonly data: any }

export type InputProps = {
	field: RowField
	error: string | null
}

export function FormieForm({
	form,
	className = '',
}: {
	className?: string
	form: FormType
}) {
	const actionData = useActionData<FormActionResult>()
	const submit = useSubmit()
	const formRef = React.useRef<HTMLFormElement>(null)
	const [formSuccess, setFormSuccess] = React.useState(false)
	const [isSending, setIsSending] = React.useState(false)
	// const transition = useTransition()
	// const isSending =
	// 	transition.state === 'submitting' &&
	// 	transition.submission.formData.get('_action') === 'send'
	const formError =
		actionData?.ok === false
			? (actionData.errors.form as string) || 'Error submitting'
			: false

	React.useEffect(() => {
		setFormSuccess(false)
		if (actionData) {
			setIsSending(false)
		}
		if (!isSending && actionData?.ok) {
			setFormSuccess(true)
			formRef.current?.reset()
		}
	}, [actionData, actionData?.ok, isSending])

	async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault()
		setIsSending(true)
		let $form = event.currentTarget
		let formData = new FormData($form)
		const recaptchaSiteKey = getReCaptchaSiteKey(form)

		if (recaptchaSiteKey) {
			const reCaptchaToken = await reCaptcha(recaptchaSiteKey)
			formData.set(GreCAPTCHA_FORM_NAME, reCaptchaToken)
			submit(formData, {
				method: 'post',
			})
		} else {
			submit(formData, {
				method: 'post',
				action: $form.getAttribute('action') ?? $form.action,
			})
		}
	}

	return (
		<Form
			method="post"
			onSubmit={handleSubmit}
			className={`${className} form contact`}
			ref={formRef}
		>
			{form.csrfToken.value && (
				<input
					type="hidden"
					name={form.csrfToken.name}
					value={form.csrfToken.value}
				/>
			)}
			{form.captchas.map((c) => (
				<input
					key={c.name}
					type="hidden"
					name={c.handle}
					data-name={c.name}
					value={c.value}
				/>
			))}
			{/* TODO: Pages */}
			{form.rows.map(
				(row) =>
					row.uid && (
						<div key={row.uid} className="row">
							{row.rowFields.map((field) => (
								<FormField field={field} key={field.uid}>
									{field.type}
								</FormField>
							))}
						</div>
					),
			)}
			<div className="row relative">
				{formError && <span className="error-message">{formError}</span>}
				{formSuccess && (
					<span className="success-message">Successfully Sent!</span>
				)}
				<ArrowButton
					name="_action"
					value="send"
					type="submit"
					disabled={isSending}
					className={`g-recaptcha col-span-6 w-full text-left ${
						isSending ? 'opacity-40' : ''
					}`}
				>
					{isSending ? 'Submitting Message' : 'Submit Message'}
				</ArrowButton>
			</div>
		</Form>
	)
}

FormieForm.displayName = 'test'
FormieForm.Heading = Heading
FormieForm.Input = Input
FormieForm.Name = InputName
FormieForm.Email = InputEmail
FormieForm.SingleLineText = InputSingleLineText
FormieForm.MultiLineText = TextareaMultiLineText
FormieForm.Phone = InputPhone
